import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslationService } from '../../../services/translation.service';

export type LoaderVariant = 'analyzing' | 'thankyou' | 'almostready';

@Component({
  selector: 'app-loader',
  standalone: true,
  imports: [],
  templateUrl: './loader.component.html',
  styleUrls: ['./loader.component.scss'],
})
export class loaderComponent implements OnInit, OnDestroy {
  /** Which loading screen to show. Drives icon, text and ring speed. */
  @Input() variant: LoaderVariant = 'analyzing';

  /** Animation only — drop the heading/subtitle/tip. Used for the photo
   *  analysis overlay, where the ring sits centred over the face image. */
  @Input() compact = false;

  /** Hide the center glyph (the eye). Set ONLY on the captured-photo analysis
   *  overlay (the ring sits over the user's own photo there) — every other
   *  loading screen keeps its center icon. */
  @Input() hideCenterIcon = false;

  /** B7 loading states: how long the wait is EXPECTED to take. Past this the
   *  loader moves to the DELAYED state and reassures her the work is not lost.
   *  Applies to every wait that renders this loader (login, face analysis,
   *  OAuth return, report generation, transcription) with no caller change. */
  @Input() delayedAfterMs = 12000;

  /** B7: the wait FAILED. The caller sets this instead of leaving her on a
   *  spinner forever; the loader then offers Retry + a support path and keeps
   *  whatever she already entered (the caller owns that input, we never clear it). */
  @Input() failed = false;

  /** B7: an optional caller-specific reason for the failure (e.g. "the photo
   *  was too small to analyse"). Replaces only the generic body line; the
   *  heading, Retry button and support line stay the same everywhere. */
  @Input() failedMessage = '';

  /** Emitted when she taps Retry in the failed state. */
  @Output() retry = new EventEmitter<void>();

  /** True once the wait passed delayedAfterMs (still working, just slow). */
  delayed = false;

  heading = '';
  subtitle = '';
  tip = '';
  delayedNote = '';
  failedHeading = '';
  failedBody = '';
  failedRetry = '';
  failedSupport = '';

  private langSub?: Subscription;
  private delayTimer?: ReturnType<typeof setTimeout>;

  constructor(private translationService: TranslationService) {}

  async ngOnInit(): Promise<void> {
    this.startDelayTimer();
    await this.translationService.waitForInit();
    this.applyText();
    this.langSub = this.translationService.currentLanguage$.subscribe(() =>
      this.applyText()
    );
  }

  onRetry(): void {
    // Restart the expected/delayed cycle for the new attempt.
    this.delayed = false;
    this.startDelayTimer();
    this.retry.emit();
  }

  private startDelayTimer(): void {
    if (this.delayTimer) clearTimeout(this.delayTimer);
    if (!this.delayedAfterMs || this.delayedAfterMs <= 0) return;
    this.delayTimer = setTimeout(() => {
      this.delayed = true;
    }, this.delayedAfterMs);
  }

  private get key(): string {
    switch (this.variant) {
      case 'thankyou':
        return 'loader.thankYou';
      case 'almostready':
        return 'loader.almostReady';
      default:
        return 'loader.analyzing';
    }
  }

  private applyText(): void {
    const t = (k: string) => this.translationService.translate(k);
    this.heading = t(`${this.key}.heading`);
    this.subtitle = t(`${this.key}.subtitle`);
    this.tip = t(`${this.key}.tip`);
    this.delayedNote = t('loader.delayedNote');
    this.failedHeading = t('loader.failed.heading');
    this.failedBody = t('loader.failed.body');
    this.failedRetry = t('loader.failed.retry');
    this.failedSupport = t('loader.failed.support');
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    if (this.delayTimer) clearTimeout(this.delayTimer);
  }
}
